/**
 * @fileoverview Tipos para configuração e instâncias do Axios
 * @module AxiosTypes
 * @version 1.0.0
 */

import type { AxiosRequestConfig, AxiosResponse } from "axios";

/**
 * URLs externas da aplicação
 *
 * @public
 */
export interface ExternalUrls {
  API_AVATAR_URL: string;
  /** Chave de autenticação para API Avatar */
  API_AVATAR_SECRET_KEY: string;
  /** Endpoint para API de borracharia */
  API_CONTROLE_URL: string;
}

/**
 * URLs internas da aplicação
 *
 * @public
 */
export interface InternalUrls {
  /** Endpoint para Acessos */
  API_USER: string;
  /** Endpoint para conteúdo da documentação */
  API_DOCS_CONTENT: string;
  /** Endpoint para caminhos da documentação */
  API_DOCS_PATHS: string;
  /** Endpoint para produtos de pré-notas */
  API_PRENOTAS_PRODUTOS: string;
  /** Endpoint para classificação */
  API_CLASSIFICACAO: string;
  /** Endpoint para produtos de classificação */
  API_CLASSIFICACAO_PRODUTOS: string;
  /** Endpoint para tabela de pré-notas */
  API_PRENOTAS_LIST: string;
  /** Endpoint para timeline de pré-notas */
  API_PRENOTAS_TIMELINE: string;
  /** Endpoint para edição de pré-notas */
  API_PRENOTAS_EDICAO: string;
  /** Endpoint para aniversariantes */
  API_ANIVERSARIO: string;
  /** Endpoint para autenticação Azure */
  API_AZURE_AUTH: string;
  /** Endpoint para dashboard de pré-notas */
  API_PRENOTAS_DASHBOARD: string;
  API_PRENOTAS_FORNECEDORES: string;
  API_PRENOTAS_PEDIDOS: string;
  API_PRENOTAS_PRE_SOLICITACAO: string;
  /** Endpoint para API Go */
  API_GO: string;
  /** Endpoint para API de produção */
  API_PRODUCTION: string;
  /** Endpoint para faturas */
  API_FINANCEIRO_FATURAS: string;
  API_FINANCEIRO_INCLUSAO: string;
  /** Endpoint para anexos de pré-notas */
  API_PRENOTAS_ANEXOS: string;
  /** Endpoint para API de produção */
  API_APPS: string;
  /** Endpoint para API de upload local */
  API_LOCAL_APPS_UPLOAD: string;

  API_PRENOTAS_EDICAO_PROD: string;
}

/**
 * Configuração completa de URLs da aplicação
 *
 * @public
 */
export interface Config {
  /** URLs externas */
  out: ExternalUrls;
  /** URLs internas */
  in: InternalUrls;
}

/**
 * Tipos de instâncias de API disponíveis
 *
 * @public
 */
export type ApiInstanceType = "production" | "avatar" | "internal";

/**
 * Configuração para criação de instâncias de API
 *
 * @public
 */
export interface ApiInstanceConfig {
  /** URL base da API */
  baseURL: string;
  /** Se deve incluir token de autenticação automaticamente */
  requiresAuth?: boolean;
  /** Token específico para esta instância (ex: Conexão Nfe) */
  staticToken?: string;
  /** Timeout padrão para requisições */
  timeout?: number;
  /** Headers adicionais */
  headers?: Record<string, string>;
}

/**
 * Mapeamento de tipos para configurações
 *
 * @public
 */
export type ApiInstancesMap = Record<ApiInstanceType, ApiInstanceConfig>;

/**
 * Função para obter token de autenticação
 *
 * @public
 */
export type TokenProvider = () => Promise<string | null>;

/**
 * Opções para requisições customizadas
 *
 * @public
 */
export interface CustomRequestOptions extends AxiosRequestConfig {
  /** Forçar uso de token específico */
  forceToken?: string;
  /** Desabilitar token automático para esta requisição */
  skipAuth?: boolean;
}

/**
 * Resposta tipada para requisições
 *
 * @public
 */
export interface ApiResponse<T = unknown> extends AxiosResponse<T> {
  /** Dados da resposta */
  data: T;
}

/**
 * Configuração do interceptor de requisições
 *
 * @public
 */
export interface RequestInterceptorConfig {
  /** Função para obter token */
  tokenProvider: TokenProvider;
  /** URLs que requerem autenticação */
  authRequiredUrls: string[];
  /** Função de log para debug */
  logger?: (message: string, data?: unknown) => void;
  staticHeaders?: () => Record<string, string>;
  /** Função para obter o Tenant ID, para adicionar ao cabeçalho da requisição. */
  tenantIdProvider?: () => string | Promise<string>;
}

/**
 * Configuração do interceptor de respostas
 *
 * @public
 */
export interface ResponseInterceptorConfig {
  /** Função para tratar erros de autenticação */
  onAuthError?: (error: unknown) => void;
  /** Função para tratar outros erros */
  onError?: (error: unknown) => void;
  /** Função de log para debug */
  logger?: (message: string, data?: unknown) => void;
  /** Função para obter token (para refresh automático) */
  tokenProvider?: TokenProvider;
}
